import React from "react";
import { IconButton, Typography, Box, Tooltip, CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsIcon from "@mui/icons-material/Collections";
import CategoryIcon from "@mui/icons-material/Category";

interface BulkActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkDelete: () => void;
  onBulkPublish: () => void;
  onBulkUnpublish: () => void;
  onBulkCategory: () => void;
  onBulkGallery: () => void;
  isLoading?: boolean;
}

export const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedCount,
  onClearSelection,
  onBulkDelete,
  onBulkPublish,
  onBulkUnpublish,
  onBulkCategory,
  onBulkGallery,
  isLoading = false,
}) => {
  if (selectedCount === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 1.5 },
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Selection Count */}
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "0.8125rem", sm: "0.8125rem" },
            color: "text.secondary",
            fontWeight: 400,
          }}
        >
          {selectedCount} {selectedCount === 1 ? "image" : "images"}
        </Typography>

        {/* Actions - Icon Only */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {isLoading && (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          )}

          <IconButton
            size="small"
            onClick={onBulkPublish}
            disabled={isLoading}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "text.primary",
                backgroundColor: "transparent",
              },
            }}
            title="Publish"
          >
            <PublicIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={onBulkUnpublish}
            disabled={isLoading}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "text.primary",
                backgroundColor: "transparent",
              },
            }}
            title="Unpublish"
          >
            <LockIcon fontSize="small" />
          </IconButton>

          <Tooltip title="Assign Categories" arrow>
            <IconButton
              size="small"
              onClick={onBulkCategory}
              disabled={isLoading}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                },
              }}
            >
              <CategoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add to Gallery" arrow>
            <IconButton
              size="small"
              onClick={onBulkGallery}
              disabled={isLoading}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                },
              }}
            >
              <CollectionsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Selected" arrow>
            <IconButton
              size="small"
              onClick={onBulkDelete}
              disabled={isLoading}
            sx={{
              color: "error.main",
              "&:hover": {
                color: "error.dark",
                backgroundColor: "transparent",
              },
            }}
            title="Delete"
          >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear Selection" arrow>
            <IconButton
              size="small"
              onClick={onClearSelection}
              sx={{
                color: "text.secondary",
                ml: { xs: 0.5, sm: 1 },
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
